import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  Shield,
  GraduationCap,
  Users as UsersIcon,
  BookOpen as BookOpenIcon,
  User as UserIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "./Logo";
import { LanguageToggle } from "./LanguageToggle";
import { NotificationsPopover } from "../NotificationsPopover";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAuth } from "@/auth/AuthProvider";

export function Header() {
  const { t } = useLanguage();
  const { user, isAdmin, isStudent, isInstructor, isParent, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  const portal = isAdmin
    ? { to: "/admin" as const, label: "Admin", icon: Shield }
    : isInstructor
      ? { to: "/instructor" as const, label: "Instructor", icon: BookOpenIcon }
      : isStudent
        ? { to: "/student" as const, label: "My Learning", icon: GraduationCap }
        : isParent
          ? { to: "/parent" as const, label: "Parent", icon: UsersIcon }
          : { to: "/dashboard" as const, label: "Dashboard", icon: LayoutDashboard };

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/courses", label: t.nav.courses },
    { to: "/learning-path", label: t.nav.path },
    { to: "/blog", label: t.nav.blog },
    { to: "/events", label: t.nav.events },
    { to: "/faq", label: t.nav.faq },
    { to: "/contact", label: t.nav.contact },
  ] as const;

  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-background/80 border-b border-border/60">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="px-3 py-2 text-sm font-medium rounded-full text-foreground/80 hover:text-primary hover:bg-primary/10 transition"
              activeProps={{ className: "text-primary bg-primary/10" }}
              activeOptions={{ exact: l.to === "/" }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && <NotificationsPopover />}
          <LanguageToggle />
          {user ? (
            <>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex rounded-full"
              >
                <Link to={portal.to}>
                  <portal.icon className="h-4 w-4 me-1" />
                  {portal.label}
                </Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex rounded-full"
              >
                <Link to="/profile">
                  <UserIcon className="h-4 w-4 me-1" /> Profile
                </Link>
              </Button>
              <Button
                onClick={() => signOut()}
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex rounded-full"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex rounded-full"
              >
                <Link to="/login">{t.nav.login}</Link>
              </Button>
              <Button
                asChild
                size="sm"
                className="hidden sm:inline-flex rounded-full bg-gradient-primary text-primary-foreground border-0 shadow-soft"
              >
                <Link to="/signup">{t.nav.signup}</Link>
              </Button>
            </>
          )}
          <button
            type="button"
            className="lg:hidden p-2 rounded-full hover:bg-muted"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border/60 bg-background">
          <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-xl text-sm font-medium hover:bg-muted"
                activeProps={{ className: "text-primary bg-primary/10" }}
                activeOptions={{ exact: l.to === "/" }}
              >
                {l.label}
              </Link>
            ))}
            {user ? (
              <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-border/50">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="w-full rounded-full justify-start"
                >
                  <Link to={portal.to} onClick={() => setOpen(false)}>
                    <portal.icon className="h-4 w-4 me-2" /> {portal.label}
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="w-full rounded-full justify-start"
                >
                  <Link to="/profile" onClick={() => setOpen(false)}>
                    <UserIcon className="h-4 w-4 me-2" /> Profile
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => {
                    signOut();
                    setOpen(false);
                  }}
                >
                  <LogOut className="h-4 w-4 me-2" /> Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-border/50">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="w-full rounded-full justify-start"
                >
                  <Link to="/login" onClick={() => setOpen(false)}>
                    {t.nav.login}
                  </Link>
                </Button>
                <Button
                  asChild
                  size="sm"
                  className="w-full rounded-full justify-start bg-gradient-primary text-primary-foreground border-0 shadow-soft"
                >
                  <Link to="/signup" onClick={() => setOpen(false)}>
                    {t.nav.signup}
                  </Link>
                </Button>
              </div>
            )}
            <Button
              asChild
              className="mt-2 rounded-full bg-gradient-primary text-primary-foreground border-0"
            >
              <Link to="/contact" onClick={() => setOpen(false)}>
                {t.cta.bookFree}
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
